import { Link } from "react-router-dom";
import { PlayCircle, Clock, Layers, User } from "lucide-react";
import { formatPrice, formatDuration } from "@/lib/api";
import { optImg } from "@/lib/page";
import { Badge } from "@/components/ui/badge";

export function CourseCard({ course, index = 0 }) {
  const upcoming = course.is_upcoming;
  const price = course.effective_price != null ? course.effective_price : course.price;
  const regular = course.regular_price != null ? course.regular_price : course.price;
  const base = upcoming ? regular : course.price;
  const hasDiscount = price < base;
  const isFree = price === 0;
  return (
    <Link
      to={`/kurslar/${course.slug}`}
      data-testid={`course-card-${course.slug}`}
      className="group relative flex flex-col bg-ink-surface border border-white/5 rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-white/15 transition-transform duration-300 ease-out animate-fade-up"
      style={{ animationDelay: `${index * 70}ms`, opacity: 0 }}
    >
      <div className="relative aspect-video overflow-hidden bg-ink-elevated">
        {course.thumbnail ? (
          <img src={optImg(course.thumbnail, 800)} alt={course.title} loading="lazy" width="800" height="450"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><PlayCircle className="w-10 h-10 text-muted-foreground" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-transparent to-transparent opacity-60" />
        {upcoming ? (
          <Badge className="absolute top-3 left-3 bg-blue-500 text-white font-bold border-0 shadow-lg text-[11px]">Yakında · Ön Kayıt</Badge>
        ) : isFree ? (
          <Badge className="absolute top-3 left-3 bg-green-500 text-white font-bold border-0 shadow-lg text-[11px]">Ücretsiz</Badge>
        ) : course.category && (
          <Badge className="absolute top-3 left-3 bg-ink/80 backdrop-blur text-foreground border-white/10 text-[11px]">{course.category}</Badge>
        )}
        {course.has_promo && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-ink/80 backdrop-blur text-foreground border border-white/10 rounded-full px-2.5 py-1 text-[11px] font-medium" data-testid={`course-has-promo-${course.course_id}`}>
            <PlayCircle className="w-3.5 h-3.5 text-gold" /> Tanıtım var
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-heading font-semibold text-base leading-snug tracking-tight text-foreground group-hover:text-gold transition-colors duration-200 line-clamp-2">
          {course.title}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">{course.subtitle}</p>

        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> {course.lesson_count} ders</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {formatDuration(course.total_seconds)}</span>
        </div>

        {course.instructor && (
          <div className="flex items-center gap-2 mt-3">
            {course.instructor.avatar ? <img src={optImg(course.instructor.avatar, 96)} alt={course.instructor.name} loading="lazy" width="24" height="24" className="w-6 h-6 rounded-full object-cover border border-white/10" /> : <span className="w-6 h-6 rounded-full bg-gold/15 flex items-center justify-center"><User className="w-3.5 h-3.5 text-gold" /></span>}
            <span className="text-xs text-muted-foreground">{course.instructor.name}</span>
          </div>
        )}

        <div className="flex items-end justify-between mt-4 pt-4 border-t border-white/5">
          <div>
            {course.sale_closed ? <span className="font-heading font-bold text-base text-gold" data-testid={`course-card-sale-closed-${course.course_id}`}>Yakında satışta</span> : <>
            {hasDiscount && <span className="text-xs text-muted-foreground line-through mr-2">{formatPrice(base)} ₺</span>}
            <span className="font-heading font-bold text-lg text-gold">
              {price === 0 ? "Ücretsiz" : `${formatPrice(price)} ₺`}
            </span>
            </>}
          </div>
          <span className="text-xs font-medium text-foreground/70 group-hover:text-gold transition-colors duration-200">{upcoming ? "Ön Kayıt →" : "İncele →"}</span>
        </div>
      </div>
    </Link>
  );
}
